import type {
  Stock,
  StockStatusEnum as StockStatus,
  ProductVariant,
} from '../shared/types';
import {
  StockSchema,
  EntityType,
  EntityBase
} from '../shared/types';
import z from 'zod';

const versionOne = 1;

export class StockEntity {

  private constructor(private readonly props: Stock) { }

  static create(props: Omit<Stock, keyof z.infer<typeof EntityBase>>): StockEntity {
    const now = new Date();
    const stock = StockSchema.parse({
      ...props,
      _id: EntityType.enum.stock + globalThis.crypto.randomUUID(),
      type: EntityType.enum.stock,
      version: versionOne,
      createdAt: now,
      updatedAt: now,
    });
    return new StockEntity(stock);
  }

  static restore(data: Stock): StockEntity {
    return new StockEntity(StockSchema.parse(data));
  }

  get _id(): string {
    return this.props._id;
  }

  get type(): string {
    return this.props.type;
  }

  get version(): number {
    return this.props.version;
  }

  get productId(): string {
    return this.props.productId;
  }

  get warehouseId(): string {
    return this.props.warehouseId;
  }

  get locationId(): string {
    return this.props.locationId;
  }

  get quantity(): number {
    return this.props.quantity;
  }

  get status(): StockStatus {
    return this.props.status;
  }

  get lastMovementDate(): Date {
    return this.props.lastMovementDate;
  }

  get lastCountDate(): Date | undefined {
    return this.props.lastCountDate;
  }

  get variantId(): string | undefined {
    return this.props.variantId;
  }

  get lotNumber(): string | undefined {
    return this.props.lotNumber;
  }

  get serialNumber(): string | undefined {
    return this.props.serialNumber;
  }

  get expirationDate(): Date | undefined {
    return this.props.expirationDate;
  }

  get palletSpaces(): number | undefined {
    return this.props.palletSpaces;
  }

  get weight(): number | undefined {
    return this.props.weight;
  }

  get dimensions(): { length: number; width: number; height: number } | undefined {
    return this.props.dimensions;
  }
  get metadata(): Record<string, any> | undefined {
    return this.props.metadata;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get updatedAt(): Date {
    return this.props.updatedAt;
  }

  private _variant?: ProductVariant;

  get variant(): ProductVariant | undefined {
    return this._variant;
  }

  set variant(value: ProductVariant | undefined) {
    this._variant = value;
  }

  isAvailable(): boolean {
    return this.status === 'available';
  }
  isReserved(): boolean {
    return this.status === 'reserved';
  }

  reserve(): void {
    if (!this.isAvailable()) {
      throw new Error('Stock is not available for reservation');
    }
    this.props.status = 'reserved';
    this.props.updatedAt = new Date();
  }

  release(): void {
    if (!this.isReserved()) {
      throw new Error('Stock is not reserved');
    }
    this.props.status = 'available';
    this.props.updatedAt = new Date();
  }

  markAsDamaged(): void {
    if (!this.isAvailable()) {
      throw new Error('Stock is not available to mark as damaged');
    }
    this.props.status = 'damaged';
    this.props.updatedAt = new Date();
  }

  setInTransit(): void {
    if (!this.isAvailable()) {
      throw new Error('Stock is not available for transit');
    }
    this.props.status = 'in_transit';
    this.props.updatedAt = new Date();
  }

  adjustQuantity(adjustment: number): void {
    const newQuantity = this.quantity + adjustment;
    if (newQuantity < 0) {
      throw new Error('Quantity cannot be negative');
    }
    this.props.quantity = newQuantity;
    this.props.updatedAt = new Date();
  }

  validate(): boolean {
    try {
      StockSchema.parse(this);
      return true;
    } catch (error) {
      return false;
    }
  }

  toJSON(): Stock {
    return StockSchema.parse(this);
  }
}

export type { Stock };
